import { supabase } from "@/integrations/supabase/client";

export const db = supabase as any;

export type PackageRecord = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  audience: string | null;
  monthly_price: number;
  currency: string;
  is_trial: boolean;
  is_enterprise: boolean;
  is_active: boolean;
  is_public?: boolean;
  is_recommended?: boolean;
  billing_period?: string;
  sort_order: number;
  trial_days: number | null;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  max_websites: number | null;
  max_articles_per_month: number | null;
  max_draft_articles: number | null;
  max_ai_credits: number | null;
  max_media_assets: number | null;
  max_team_members: number | null;
  storage_mb: number | null;
  bandwidth_mb: number | null;
  publishing_methods: string[];
  support_level: string;
  features: Record<string, unknown>;
  name_fr?: string | null;
  name_en?: string | null;
  description_fr?: string | null;
  description_en?: string | null;
  audience_fr?: string | null;
  audience_en?: string | null;
  marketing_features_fr?: string[] | null;
  marketing_features_en?: string[] | null;
  cta_label_fr?: string | null;
  cta_label_en?: string | null;
};

export type SubscriptionRecord = {
  id: string;
  user_id: string;
  package_id: string | null;
  status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  latest_invoice_id?: string | null;
  stripe_price_id: string | null;
  current_period_start?: string | null;
  current_period_end: string | null;
  renewal_date?: string | null;
  billing_cycle?: string | null;
  payment_status?: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
  auto_renew?: boolean;
  canceled_at?: string | null;
  manual_limit_overrides: Record<string, unknown>;
  packages?: PackageRecord | null;
};

export type InvoiceRecord = {
  id: string;
  user_id: string | null;
  package_id: string | null;
  subscription_id?: string | null;
  stripe_invoice_id: string;
  invoice_number: string | null;
  stripe_checkout_session_id?: string | null;
  stripe_payment_intent_id?: string | null;
  amount_due: number;
  amount_paid: number;
  subtotal_amount?: number;
  total_amount?: number;
  currency: string;
  status: string;
  billing_cycle?: string | null;
  due_date: string | null;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  generated_invoice_pdf_base64?: string | null;
  generated_invoice_pdf_filename?: string | null;
  invoice_email_status?: string | null;
  invoice_email_sent_at?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_company?: string | null;
  package_name?: string | null;
  billing_period_start?: string | null;
  billing_period_end?: string | null;
  renewal_date?: string | null;
  tax_amount?: number;
  company_details?: Record<string, unknown>;
  packages?: Pick<PackageRecord, "name"> | null;
};

export type UsageRecord = {
  articles_created: number;
  articles_published: number;
  websites_created: number;
  ai_credits_used: number;
  media_assets_used: number;
  team_members_used: number;
  storage_mb_used: number;
  bandwidth_mb_used: number;
};

export const statusTone: Record<string, string> = {
  trialing: "bg-info/15 text-info border-info/20",
  active: "bg-success/15 text-success border-success/20",
  paid: "bg-success/15 text-success border-success/20",
  open: "bg-warning/15 text-warning border-warning/20",
  past_due: "bg-warning/15 text-warning border-warning/20",
  unpaid: "bg-destructive/15 text-destructive border-destructive/20",
  failed: "bg-destructive/15 text-destructive border-destructive/20",
  canceled: "bg-muted text-muted-foreground border-border",
  cancelled: "bg-muted text-muted-foreground border-border",
  refunded: "bg-info/15 text-info border-info/20",
  incomplete: "bg-warning/15 text-warning border-warning/20",
  incomplete_expired: "bg-muted text-muted-foreground border-border",
  draft: "bg-muted text-muted-foreground border-border",
  void: "bg-muted text-muted-foreground border-border",
  uncollectible: "bg-destructive/15 text-destructive border-destructive/20",
};

export function money(centsOrUnits: number, currency = "eur", fromCents = false, locale = "fr-FR") {
  const amount = fromCents ? centsOrUnits / 100 : centsOrUnits;
  return new Intl.NumberFormat(locale, { style: "currency", currency: currency.toUpperCase() }).format(amount);
}

export function formatDate(value?: string | null) {
  if (!value) return "Not set";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium" }).format(new Date(value));
}

export function limitValue(value: number | null | undefined) {
  return value == null ? "Custom" : value.toLocaleString();
}

export function invoiceTotalCents(invoice: Pick<InvoiceRecord, "total_amount" | "amount_due" | "amount_paid">) {
  return invoice.total_amount || invoice.amount_due || invoice.amount_paid || 0;
}

export function invoiceDownloadHref(invoice: Pick<InvoiceRecord, "generated_invoice_pdf_base64" | "invoice_pdf">) {
  return invoice.generated_invoice_pdf_base64
    ? `data:application/pdf;base64,${invoice.generated_invoice_pdf_base64}`
    : invoice.invoice_pdf;
}

export function shouldSkipInvoiceEmail(existingLog: { invoice_id?: string | null; email_type?: string | null } | null | undefined, invoiceId: string, emailType: string) {
  return Boolean(existingLog?.invoice_id === invoiceId && existingLog.email_type === emailType);
}

export const packageFeatureLabels: Record<string, string> = {
  live_publishing: "Live publishing",
  custom_domain: "Custom domain",
  automation: "Automation",
  wordpress_publishing: "Multi-site publishing",
  plesk_provisioning: "Plesk provisioning",
  translation: "Translation",
  seo: "SEO tools",
  api_embed: "API/embed access",
  watermark: "Watermarked previews",
};
